/**
 * «Ver como» — a troca entre a Genesis e as vistas do portal (issue #584; Stories 35.9
 * e 35.10).
 *
 * Vive DENTRO do menu do usuário dos dois shells (o rodapé da sidebar interna e o
 * `PortalUserMenu`), e só aparece a quem tem o que escolher: o perfil de cliente
 * (membro de um cliente) e/ou os clientes de que é gestora de conta. O gestor de conta
 * substituiu o suporte, que abria qualquer cliente ativo: a lista vem da `/api/me`
 * (`portalViews.managedCustomers`), já só com os ativos, e escolhe-se no perfil.
 *
 * A TROCA É UM RECARREGAMENTO, e não uma navegação do router: os dois shells
 * assumem que se chega a eles por um documento novo (`html[data-surface]`, o
 * `--aur-h` do `AppLayout`), e as caches do portal de um cliente não podem
 * sobreviver para dentro do de outro. A vista é da ABA (`lib/portal-view.ts`).
 *
 * OS CLIENTES GERIDOS FICAM AQUI DENTRO, numa lista em linha que rola, e não num
 * diálogo por cima: o menu é um overlay de vidro, e um segundo overlay aberto a
 * partir dele seria um popper dentro de `backdrop-filter` (o recorte que o
 * `vidro-do-chrome.test.ts` documenta) ou um menu que fecha por baixo do diálogo.
 *
 * O OWNER (Story 35.10) abre qualquer cliente ativo, e entra sempre como owner: em vez
 * do perfil e dos geridos, tem um item que abre a lista em linha de TODOS os clientes
 * ativos, com busca (`EscolhaDoOwner`). É o desenho do suporte de antes da 35.9, e a
 * lista é a de `GET /api/customers`, que o admin já lê. Só monta para o owner: quem não
 * o é não faz a consulta.
 */
import { useMemo, useState, type ReactElement, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Briefcase, Building2, Check, Crown, LayoutDashboard, type LucideIcon } from 'lucide-react'
import type { PortalMode, PortalViews } from '@caliber/shared'

import { SearchField } from '@/components/lumen/search-field'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { listCustomers } from '@/lib/api/customers'
import { choosePortalView, leavePortalView } from '@/lib/portal-view'
import { GENESIS_HOME, PORTAL_HOME } from '@/lib/roles'
import { cn } from '@/lib/utils'

export interface PortalViewSwitcherProps {
  views: PortalViews
  /** Onde a aba está: na Genesis (`null`), ou na vista de um cliente. */
  current: { customerId: string; customerName: string; mode: PortalMode } | null
  /** Fecha o menu — antes de a página recarregar, ou quando nada muda. */
  onNavigate: () => void
}

function Item({
  icon: Icon,
  label,
  hint,
  atual,
  expandido,
  onSelect,
}: {
  icon: LucideIcon
  label: ReactNode
  hint?: string
  atual: boolean
  /** Só no item que abre a lista do owner. */
  expandido?: boolean
  onSelect: () => void
}): ReactElement {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={atual ? 'true' : undefined}
      aria-expanded={expandido}
      className="text-ink hover:bg-sunk flex w-full items-center gap-2.5 rounded-sm px-2.5 py-2 text-left text-[13px] font-medium transition-colors"
    >
      <Icon className="text-ink-3 size-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1 leading-tight">
        <span className="block truncate">{label}</span>
        {hint ? <span className="text-ink-3 block truncate text-[11px]">{hint}</span> : null}
      </span>
      {atual ? <Check className="text-ink-2 size-4 shrink-0" aria-hidden="true" /> : null}
    </button>
  )
}

/**
 * O owner escolhe entre todos os clientes ativos (Story 35.10). O da vista atual fica de
 * fora da lista, porque já está marcado em cima; o do perfil de cliente entra, porque o
 * owner entra nele como owner.
 */
function EscolhaDoOwner({
  current,
  irParaCliente,
  onNavigate,
}: {
  current: PortalViewSwitcherProps['current']
  irParaCliente: (customerId: string) => void
  onNavigate: () => void
}): ReactElement {
  const [escolhendo, setEscolhendo] = useState(false)
  const [busca, setBusca] = useState('')

  const clientes = useQuery({
    queryKey: ['customers', 'portal-view-picker'],
    queryFn: ({ signal }) => listCustomers({ status: 'active', pageSize: 200, signal }),
    enabled: escolhendo,
  })

  const opcoes = useMemo(() => {
    const termo = busca.trim().toLocaleLowerCase('pt-BR')
    return (clientes.data?.items ?? []).filter(
      (c) =>
        c.id !== current?.customerId &&
        (termo === '' || c.name.toLocaleLowerCase('pt-BR').includes(termo)),
    )
  }, [clientes.data, busca, current])

  const naVista = current?.mode === 'owner'

  return (
    <>
      {naVista ? (
        <Item icon={Crown} label={`Owner · ${current.customerName}`} atual onSelect={onNavigate} />
      ) : null}
      <Item
        icon={Crown}
        label={naVista ? 'Trocar de cliente…' : 'Abrir um cliente como owner…'}
        hint="Todos os clientes ativos"
        atual={false}
        expandido={escolhendo}
        onSelect={() => setEscolhendo((v) => !v)}
      />

      {escolhendo ? (
        <div className="px-1.5 pb-1 pt-1.5">
          <SearchField
            value={busca}
            onChange={setBusca}
            label="Buscar cliente"
            placeholder="Buscar cliente"
            className="w-full"
          />
          <div className="mt-1.5 max-h-56 overflow-y-auto">
            {clientes.isPending ? (
              <div className="space-y-1.5 p-1" aria-busy="true">
                <Skeleton className="h-7 w-full" />
                <Skeleton className="h-7 w-5/6" />
                <Skeleton className="h-7 w-4/6" />
              </div>
            ) : clientes.isError ? (
              <div className="space-y-1.5 p-1">
                <p role="alert" className="text-error text-[12px]">
                  Não foi possível carregar os clientes.
                </p>
                <Button size="sm" variant="secondary" onClick={() => void clientes.refetch()}>
                  Tentar de novo
                </Button>
              </div>
            ) : opcoes.length === 0 ? (
              <p className="text-ink-2 p-1.5 text-[12px]">
                {busca.trim() === ''
                  ? 'Nenhum outro cliente ativo.'
                  : 'Nenhum cliente com esse nome.'}
              </p>
            ) : (
              opcoes.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => irParaCliente(c.id)}
                  className={cn(
                    'text-ink hover:bg-sunk block w-full truncate rounded-sm px-2.5 py-1.5 text-left',
                    'text-[13px] transition-colors',
                  )}
                >
                  {c.name}
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </>
  )
}

export function PortalViewSwitcher({
  views,
  current,
  onNavigate,
}: PortalViewSwitcherProps): ReactElement {
  const perfil = views.clientCustomer
  // O owner entra sempre como owner (Story 35.10): a API já não lhe dá os modos de
  // perfil nem de gestor, e os itens deles enganariam.
  const owner = views.owner === true

  function irParaGenesis(): void {
    onNavigate()
    if (current === null) return
    leavePortalView()
    window.location.assign(GENESIS_HOME)
  }

  function irParaCliente(customerId: string): void {
    onNavigate()
    if (current?.customerId === customerId) return
    choosePortalView(customerId)
    window.location.assign(PORTAL_HOME)
  }

  return (
    <div>
      <p className="eyebrow px-1.5 pb-1.5 pt-0.5">Ver como</p>

      <Item
        icon={LayoutDashboard}
        label="Genesis Caliber"
        atual={current === null}
        onSelect={irParaGenesis}
      />

      {owner ? (
        <EscolhaDoOwner current={current} irParaCliente={irParaCliente} onNavigate={onNavigate} />
      ) : null}

      {!owner && perfil ? (
        <Item
          icon={Building2}
          label={perfil.name}
          hint="Perfil de cliente"
          atual={current?.mode === 'member'}
          onSelect={() => irParaCliente(perfil.id)}
        />
      ) : null}

      {!owner && views.managedCustomers.length > 0 ? (
        <div className="max-h-56 overflow-y-auto">
          {views.managedCustomers.map((c) => (
            <Item
              key={c.id}
              icon={Briefcase}
              label={c.name}
              hint="Gestor de conta"
              atual={current?.mode === 'account_manager' && current.customerId === c.id}
              onSelect={() => irParaCliente(c.id)}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}
